import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Puzzle,
  Settings,
  ArrowRight,
  HeartPulse,
  Layers,
  SlidersHorizontal,
  Shield,
  Bot,
  ListChecks,
  KeyRound,
  UserCog,
  GitBranch,
  Boxes,
  Radio,
  Globe,
  Database,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { SectionNav } from '@reflex/ui/components/layout/SectionNav';
import { PageLayout } from '@/components/layout/PageLayout';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { usePlugins } from '@/hooks/config/usePlugins';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { BackLink } from '@/components/shared/BackLink';
import type { PluginTableSchema } from '@/lib/plugin-registry';
import {
  buildPluginWirings,
  PluginHeroSection,
  PluginHealthSection,
  PluginTypeSection,
  PluginAuthSection,
  PluginAgentSection,
  PluginSetupStepsSection,
  PluginSecretsSection,
  PluginServiceConfigSection,
  PluginServiceAccountsSection,
  PluginDependenciesSection,
  PluginBlueprintSection,
  PluginWebhookSection,
  PluginCapabilitiesSection,
  PluginWebContributions,
  PluginDatabaseSection,
} from './plugin-detail';
import { ROUTES } from '@/lib/routes';

const pluginBackLink = <BackLink to={ROUTES.admin.plugins}>Plugins</BackLink>;

export function AdminPluginDetailPage() {
  const { pluginName } = useParams<{ pluginName: string }>();
  const { manifests, isLoading } = usePlugins();
  const plugin = manifests.find((p) => p.name === pluginName);
  const wirings = useMemo(
    () => (plugin ? buildPluginWirings(plugin, manifests) : []),
    [plugin, manifests],
  );

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader left={pluginBackLink} />
        <LoadingState message="Loading plugin..." />
      </div>
    );
  }

  if (!plugin) {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader left={pluginBackLink} />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
          <Puzzle className="h-8 w-8 text-muted-foreground/40" />
          <PageDescription className="mt-0 text-center text-sm">Plugin not found</PageDescription>
          <Link to={ROUTES.admin.plugins} className="text-xs underline hover:text-foreground">
            Back to plugins
          </Link>
        </div>
      </div>
    );
  }

  const secrets = plugin.server?.requiredSecrets ?? [];
  const serviceConfig = plugin.server?.serviceConfig;
  const serviceAccounts = plugin.server?.serviceAccounts ?? [];
  const setupSteps = plugin.server?.setupSteps ?? [];
  const blueprintContributions = plugin.server?.blueprintContributions;
  const tables: PluginTableSchema[] = plugin.server?.tables ?? [];
  const hasServiceSetup =
    setupSteps.length > 0 || secrets.length > 0 || !!plugin.web?.adminSetupComponent;

  const navSections = [
    { id: 'hero', label: 'Overview', icon: Puzzle },
    { id: 'health', label: 'Health', icon: HeartPulse },
    { id: 'type', label: 'Type', icon: Layers },
    ...(serviceConfig && serviceConfig.fields.length > 0
      ? [{ id: 'service-config', label: 'Service Config', icon: SlidersHorizontal }]
      : []),
    ...(plugin.server?.authProvider ? [{ id: 'auth', label: 'Authentication', icon: Shield }] : []),
    ...(plugin.web?.agentType ? [{ id: 'agent', label: 'Agent', icon: Bot }] : []),
    ...(setupSteps.length > 0
      ? [{ id: 'setup-steps', label: 'Setup Steps', icon: ListChecks }]
      : []),
    ...(secrets.length > 0 ? [{ id: 'secrets', label: 'Secrets', icon: KeyRound }] : []),
    ...(serviceAccounts.length > 0
      ? [{ id: 'service-accounts', label: 'Service Accounts', icon: UserCog }]
      : []),
    ...((plugin.dependencies?.length ?? 0) > 0
      ? [{ id: 'dependencies', label: 'Dependencies', icon: GitBranch }]
      : []),
    ...((blueprintContributions?.systemSetupCommands?.length ?? 0) > 0
      ? [{ id: 'blueprint', label: 'Blueprint', icon: Boxes }]
      : []),
    ...(wirings.length > 0 ? [{ id: 'webhooks', label: 'Webhooks', icon: Radio }] : []),
    { id: 'capabilities', label: 'Capabilities', icon: Puzzle },
    { id: 'web-contributions', label: 'Web UI', icon: Globe },
    ...(tables.length > 0 ? [{ id: 'database', label: 'Database', icon: Database }] : []),
  ];

  return (
    <div className="flex h-full flex-col">
      <PanelHeader left={pluginBackLink} />

      <PageLayout maxWidth="full">
        <div className="mx-auto flex w-full max-w-6xl gap-10">
          <div className="min-w-0 flex-1 max-w-4xl">
            <div className="space-y-8">
              <div id="hero" className="scroll-mt-24">
                <PluginHeroSection plugin={plugin} />
              </div>

              {hasServiceSetup && (
                <div
                  data-testid="admin-plugin-service-setup-cta"
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/30 px-4 py-3"
                >
                  <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
                    <Settings className="h-4 w-4 shrink-0" />
                    <span>
                      Service-level configuration for this plugin lives on a dedicated page.
                    </span>
                  </div>
                  <Button asChild variant="outline" size="sm" className="shrink-0 gap-1.5">
                    <Link
                      to={ROUTES.admin.pluginSetup(plugin.name)}
                      aria-label={`Open service setup for ${plugin.name}`}
                    >
                      Open service setup
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </Button>
                </div>
              )}

              <div id="health" className="scroll-mt-24">
                <PluginHealthSection
                  pluginName={plugin.name}
                  healthChecks={plugin.server?.healthChecks}
                  autoRunDependencyCheckOnLoad
                />
              </div>

              <div id="type" className="scroll-mt-24">
                <PluginTypeSection plugin={plugin} />
              </div>

              {serviceConfig && serviceConfig.fields.length > 0 && (
                <div id="service-config" className="scroll-mt-24">
                  <PluginServiceConfigSection
                    pluginName={plugin.name}
                    fields={serviceConfig.fields}
                    values={serviceConfig.values}
                  />
                </div>
              )}

              {plugin.server?.authProvider && (
                <div id="auth" className="scroll-mt-24">
                  <PluginAuthSection plugin={plugin} />
                </div>
              )}

              {plugin.web?.agentType && (
                <div id="agent" className="scroll-mt-24">
                  <PluginAgentSection plugin={plugin} />
                </div>
              )}

              {setupSteps.length > 0 && (
                <div id="setup-steps" className="scroll-mt-24">
                  <PluginSetupStepsSection steps={setupSteps} />
                </div>
              )}

              {secrets.length > 0 && (
                <div id="secrets" className="scroll-mt-24">
                  <PluginSecretsSection secrets={secrets} pluginName={plugin.name} />
                </div>
              )}

              {serviceAccounts.length > 0 && (
                <div id="service-accounts" className="scroll-mt-24">
                  <PluginServiceAccountsSection serviceAccounts={serviceAccounts} />
                </div>
              )}

              {(plugin.dependencies?.length ?? 0) > 0 && (
                <div id="dependencies" className="scroll-mt-24">
                  <PluginDependenciesSection dependencies={plugin.dependencies!} />
                </div>
              )}

              {(blueprintContributions?.systemSetupCommands?.length ?? 0) > 0 && (
                <div id="blueprint" className="scroll-mt-24">
                  <PluginBlueprintSection contributions={blueprintContributions!} />
                </div>
              )}

              {wirings.length > 0 && (
                <div id="webhooks" className="scroll-mt-24">
                  <PluginWebhookSection wirings={wirings} />
                </div>
              )}

              <div id="capabilities" className="scroll-mt-24">
                <PluginCapabilitiesSection plugin={plugin} />
              </div>

              <div id="web-contributions" className="scroll-mt-24">
                <PluginWebContributions plugin={plugin} />
              </div>

              {tables.length > 0 && (
                <div id="database" className="scroll-mt-24">
                  <PluginDatabaseSection tables={tables} />
                </div>
              )}
            </div>
          </div>
          <aside className="hidden w-44 shrink-0 xl:block">
            <div className="sticky top-32">
              <SectionNav sections={navSections} data-testid="admin-plugin-section-nav" />
            </div>
          </aside>
        </div>
      </PageLayout>
    </div>
  );
}
